import React from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CHART_COLORS, opsChartCard, PIE_PALETTE, statusToChartColor } from './operationsUi';
import { OpsChartTitle, OpsStatusBadge, OpsTableHeader } from './OpsMetricLabels';
import OperationsInfoTip from './OperationsInfoTip';

const chartTooltipStyle = {
  contentStyle: {
    backgroundColor: '#111827',
    border: '1px solid #374151',
    borderRadius: '12px',
    fontSize: '12px',
  },
  labelStyle: { color: '#9ca3af' },
};

export function HealthScoreGauge({
  score,
  label,
  infoKey = 'healthScore',
}: {
  score: number;
  label: string;
  infoKey?: string;
}) {
  const data = [
    { name: 'score', value: Math.min(100, Math.max(0, score)) },
    { name: 'rest', value: Math.max(0, 100 - score) },
  ];
  const color =
    score >= 85 ? CHART_COLORS.healthy : score >= 60 ? CHART_COLORS.warning : CHART_COLORS.critical;

  return (
    <div className={`${opsChartCard} flex flex-col items-center`}>
      <OpsChartTitle title={label} infoKey={infoKey} />
      <div className="relative h-44 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={58}
              outerRadius={72}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
              stroke="none"
            >
              <Cell fill={color} />
              <Cell fill="#e5e7eb" className="dark:fill-gray-700" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-bold tabular-nums text-gray-900 dark:text-white">{score}%</span>
        </div>
      </div>
    </div>
  );
}

export function CategoryPieChart({
  title,
  data,
  infoKey = 'errorCategoryPie',
}: {
  title: string;
  data: Array<{ name: string; value: number }>;
  infoKey?: string;
}) {
  const filtered = data.filter((d) => d.value > 0);

  return (
    <div className={opsChartCard}>
      <OpsChartTitle title={title} infoKey={infoKey} />
      {filtered.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500">No data in this period</p>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={filtered}
                cx="50%"
                cy="50%"
                innerRadius={48}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
                nameKey="name"
                label={({ name, percent }) => `${name} ${((percent ?? 0) * 100).toFixed(0)}%`}
                labelLine={false}
              >
                {filtered.map((_, i) => (
                  <Cell key={i} fill={PIE_PALETTE[i % PIE_PALETTE.length]} />
                ))}
              </Pie>
              <Tooltip {...chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function ServiceHealthPie({
  title,
  items,
  infoKey = 'serviceHealthPie',
}: {
  title: string;
  items: Array<{ name: string; status: string }>;
  infoKey?: string;
}) {
  const counts = { healthy: 0, warning: 0, critical: 0, other: 0 };
  for (const item of items) {
    if (item.status === 'healthy') counts.healthy += 1;
    else if (item.status === 'warning') counts.warning += 1;
    else if (item.status === 'critical' || item.status === 'down') counts.critical += 1;
    else counts.other += 1;
  }
  const data = [
    { name: 'Healthy', value: counts.healthy, color: CHART_COLORS.healthy },
    { name: 'Warning', value: counts.warning, color: CHART_COLORS.warning },
    { name: 'Critical', value: counts.critical, color: CHART_COLORS.critical },
    { name: 'Unknown', value: counts.other, color: CHART_COLORS.slate },
  ].filter((d) => d.value > 0);

  return (
    <div className={opsChartCard}>
      <OpsChartTitle title={title} infoKey={infoKey} />
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" outerRadius={80} dataKey="value" nameKey="name" label>
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip {...chartTooltipStyle} />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function ResourceBarChart({
  title,
  items,
  infoKey = 'resourceUtilization',
}: {
  title: string;
  items: Array<{ name: string; value: number | null; max?: number }>;
  infoKey?: string;
}) {
  const data = items
    .filter((i) => i.value != null)
    .map((i) => ({
      name: i.name,
      value: i.value as number,
      fill: (i.value as number) > 85 ? CHART_COLORS.critical : (i.value as number) > 70 ? CHART_COLORS.warning : CHART_COLORS.primary,
    }));

  return (
    <div className={opsChartCard}>
      <OpsChartTitle title={title} infoKey={infoKey} />
      {data.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500">Metrics unavailable (GCP / local)</p>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-20" horizontal={false} />
              <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <YAxis type="category" dataKey="name" width={72} tick={{ fontSize: 11 }} />
              <Tooltip {...chartTooltipStyle} formatter={(v: number) => [`${v}%`, 'Usage']} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={28}>
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function TrendAreaChart({
  title,
  data,
  dataKey,
  color = CHART_COLORS.primary,
  infoKey = 'apiLatencyTrend',
}: {
  title: string;
  data: Array<{ label: string; value: number }>;
  dataKey?: string;
  color?: string;
  infoKey?: string;
}) {
  return (
    <div className={opsChartCard}>
      <OpsChartTitle title={title} infoKey={infoKey} />
      {data.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500">No trend data</p>
      ) : (
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data}>
              <defs>
                <linearGradient id={`grad-${title}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip {...chartTooltipStyle} />
              <Area
                type="monotone"
                dataKey={dataKey || 'value'}
                stroke={color}
                fill={`url(#grad-${title})`}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function ApiLatencyBarChart({
  title,
  endpoints,
  infoKey = 'endpointLatency',
}: {
  title: string;
  endpoints: Array<{ name: string; responseTimeMs: number; status: string }>;
  infoKey?: string;
}) {
  const data = endpoints.map((e) => ({
    name: e.name.length > 18 ? `${e.name.slice(0, 16)}…` : e.name,
    ms: e.responseTimeMs,
    fill: statusToChartColor(e.status),
  }));

  return (
    <div className={opsChartCard}>
      <OpsChartTitle title={title} infoKey={infoKey} />
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ bottom: 48 }}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
            <XAxis dataKey="name" tick={{ fontSize: 9 }} angle={-35} textAnchor="end" height={56} />
            <YAxis tick={{ fontSize: 11 }} unit="ms" />
            <Tooltip {...chartTooltipStyle} formatter={(v: number) => [`${v} ms`, 'Latency']} />
            <Bar dataKey="ms" radius={[6, 6, 0, 0]} maxBarSize={36}>
              {data.map((entry, i) => (
                <Cell key={i} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function UptimeDonut({
  label,
  availabilityPercent,
  totalRequests,
  infoKey = 'uptimeDonut',
}: {
  label: string;
  availabilityPercent: number | null;
  totalRequests: number;
  infoKey?: string;
}) {
  const pct = availabilityPercent ?? 0;
  const data = [
    { name: 'up', value: pct },
    { name: 'down', value: Math.max(0, 100 - pct) },
  ];
  const color = pct >= 99 ? CHART_COLORS.healthy : pct >= 95 ? CHART_COLORS.warning : CHART_COLORS.critical;

  return (
    <div className={`${opsChartCard} text-center`}>
      <div className="mb-2 flex items-center justify-center gap-1.5">
        <p className="text-xs font-medium uppercase tracking-wide text-gray-400">{label}</p>
        <OperationsInfoTip metricKey={infoKey} title={label} />
      </div>
      <div className="relative mx-auto mt-2 h-36 w-full max-w-[160px]">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={42}
              outerRadius={56}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
              stroke="none"
            >
              <Cell fill={availabilityPercent != null ? color : CHART_COLORS.slate} />
              <Cell fill="#e5e7eb" className="dark:fill-gray-700" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <span className="text-xl font-bold tabular-nums">{availabilityPercent != null ? `${pct}%` : '—'}</span>
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500">{totalRequests.toLocaleString()} requests</p>
    </div>
  );
}

export function NotificationChannelChart({
  channels,
  infoKey = 'notificationChannels',
}: {
  channels: Record<string, { pending: number | null; sent: number | null; failed: number | null }>;
  infoKey?: string;
}) {
  const data = Object.entries(channels).map(([name, stats]) => ({
    name,
    sent: stats.sent ?? 0,
    failed: stats.failed ?? 0,
    pending: stats.pending ?? 0,
  }));

  return (
    <div className={opsChartCard}>
      <OpsChartTitle title="Notifications by channel (24h)" infoKey={infoKey} />
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
            <XAxis dataKey="name" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip {...chartTooltipStyle} />
            <Legend wrapperStyle={{ fontSize: '11px' }} />
            <Bar dataKey="sent" stackId="a" fill={CHART_COLORS.healthy} name="Sent" radius={[0, 0, 0, 0]} />
            <Bar dataKey="failed" stackId="a" fill={CHART_COLORS.critical} name="Failed" />
            <Bar dataKey="pending" stackId="a" fill={CHART_COLORS.warning} name="Pending" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export function QueueBarChart({
  queues,
  infoKey = 'queueDepth',
}: {
  queues: Array<{ name: string; pending: number; running: number; failed: number }>;
  infoKey?: string;
}) {
  const data = queues.map((q) => ({
    name: q.name.replace(' Queue', ''),
    pending: q.pending,
    running: q.running,
    failed: q.failed,
  }));

  return (
    <div className={opsChartCard}>
      <OpsChartTitle title="Queue depth" infoKey={infoKey} />
      {data.length === 0 ? (
        <p className="py-12 text-center text-sm text-gray-500">No queues</p>
      ) : (
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
              <XAxis dataKey="name" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip {...chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: '11px' }} />
              <Bar dataKey="pending" fill={CHART_COLORS.warning} name="Pending" radius={[4, 4, 0, 0]} />
              <Bar dataKey="running" fill={CHART_COLORS.primary} name="Running" />
              <Bar dataKey="failed" fill={CHART_COLORS.critical} name="Failed" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

export function StatusBadgeInline({ status, showInfo = true }: { status: string; showInfo?: boolean }) {
  const color = statusToChartColor(status);
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium capitalize"
      style={{ backgroundColor: `${color}18`, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {showInfo ? <OpsStatusBadge status={status} showInfo /> : status}
    </span>
  );
}

export function DataTable({
  headers,
  columnKeys,
  rows,
  emptyMessage = 'No data available',
}: {
  headers: string[];
  columnKeys?: string[];
  rows: Array<Array<React.ReactNode>>;
  emptyMessage?: string;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800/80">
          <tr>
            {headers.map((h, i) => (
              <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {columnKeys?.[i] ? <OpsTableHeader label={h} columnKey={columnKeys[i]} /> : h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.length === 0 ? (
            <tr>
              <td colSpan={headers.length} className="px-4 py-10 text-center text-gray-500">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, idx) => (
              <tr key={idx} className="bg-white dark:bg-gray-900/40">
                {row.map((cell, cellIdx) => (
                  <td key={cellIdx} className="px-4 py-3 text-gray-800 dark:text-gray-200">
                    {cell}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
